import { Middleware, MiddlewareAPI, PayloadAction } from '@reduxjs/toolkit';

import type { Asset } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import {
  FeatureFlag,
  type Organization,
} from '@/shared/api/protocol-ts/model/dto_organization_pb';
import type { Patient } from '@/shared/api/protocol-ts/model/dto_patient_pb';
import type { Condition } from '@/shared/api/protocol-ts/model/dto_report_condition_pb';
import type { ToothLandmark } from '@/shared/api/protocol-ts/model/dto_report_landmark_pb';
import type { Report } from '@/shared/api/protocol-ts/model/dto_report_pb';
import type { Tooth } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import type { Study } from '@/shared/api/protocol-ts/model/dto_study_pb';
import type { UploadSession } from '@/shared/api/protocol-ts/model/dto_upload_session_pb';
import type { User } from '@/shared/api/protocol-ts/model/dto_user_pb';
import { MaskGroup } from '@/shared/config';
import { debug } from '@/shared/lib';

import { assetsModel } from '@/entities/assets';
import { conditionModel, dedupeConditionsByID } from '@/entities/condition';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { reportsModel } from '@/entities/reports';
import { studyModel } from '@/entities/study';
import { toothModel } from '@/entities/tooth';
import { toothLandmarksModel } from '@/entities/toothLandmarks';
import { userModel } from '@/entities/user';

import { maskFiltersModel } from '@/features/renderMasks';

import type { AppDispatch, RootState } from '@/app/model/store';

type EntityDeletionIds = {
  DeletedToothIDs?: string[];
  DeletedConditionIDs?: string[];
  DeletedAssetIDs?: string[];
  DeletedTeethLandmarkIDs?: string[];
  DeletedStudyIDs?: string[];
  DeletedReportIDs?: string[];
};

type ThunkPayload = {
  // Common entity fields
  Report?: Report;
  Reports?: Report[];
  Condition?: Condition;
  Tooth?: Tooth;
  Teeth?: Tooth[];
  Conditions?: Condition[];
  TeethConditions?: Condition[];
  ToothLandmarks?: ToothLandmark[];
  TeethLandmarks?: ToothLandmark[];
  AllReportTeeth?: Tooth[];
  Asset?: Asset;
  Assets?: Asset[];
  ReportAssets?: Asset[];
  Patient?: Patient;
  ChangedPatient?: Patient;
  Organization?: Organization;
  User?: User;
  Study?: Study;
  Studies?: Study[];
  UploadSession?: UploadSession;
  // Deletion fields
  DeletedToothIDs?: string[];
  DeletedConditionIDs?: string[];
  DeletedAssetIDs?: string[];
  DeletedTeethLandmarkIDs?: string[];
  DeletedStudyIDs?: string[];
  DeletedReportIDs?: string[];
  // Special fields for deleteAsset
  Result?:
    | { case: 'User'; value: User }
    | { case: 'Organization'; value: Organization }
    | { case: 'Tooth'; value: Tooth };
};

const dispatchEntityDeletions = (
  store: MiddlewareAPI<AppDispatch, RootState>,
  deletionIds: EntityDeletionIds,
) => {
  deletionIds.DeletedToothIDs?.forEach((id) => {
    store.dispatch(toothModel.actions.removeOne(id));
  });
  deletionIds.DeletedConditionIDs?.forEach((id) => {
    store.dispatch(conditionModel.actions.removeOne(id));
  });
  deletionIds.DeletedAssetIDs?.forEach((id) => {
    store.dispatch(assetsModel.actions.removeOne(id));
  });
  deletionIds.DeletedTeethLandmarkIDs?.forEach((id) => {
    store.dispatch(toothLandmarksModel.actions.removeOne(id));
  });
  deletionIds.DeletedStudyIDs?.forEach((id) => {
    store.dispatch(studyModel.actions.removeOne(id));
  });
  deletionIds.DeletedReportIDs?.forEach((id) => {
    store.dispatch(reportsModel.actions.removeOne(id));
  });
};

const syncMaskFiltersWithReportSettings = (
  store: MiddlewareAPI<AppDispatch, RootState>,
  report: Report,
) => {
  const settings = report?.Settings;

  if (!settings) {
    return;
  }

  const currentFilters = store.getState().maskFilters.filters;
  const targetStates: Record<MaskGroup, boolean> = {
    perio: settings.ShowPerio ?? true,
    restorative: settings.ShowRestorative ?? true,
    endo: settings.ShowEndo ?? true,
    chart: settings.ShowAnatomy ?? true,
  };

  const MASK_GROUPS: MaskGroup[] = ['perio', 'restorative', 'endo', 'chart'];

  MASK_GROUPS.forEach((group) => {
    if (currentFilters[group].isActive !== targetStates[group]) {
      store.dispatch(maskFiltersModel.actions.toggleFilter(group));
    }
  });
};

const syncReportState = (
  store: MiddlewareAPI<AppDispatch, RootState>,
  report?: Report,
) => {
  if (!report) {
    return;
  }

  store.dispatch(reportsModel.actions.setNewestOne(report));

  if (store.getState().reports.currentReport?.ID === report.ID) {
    store.dispatch(reportsModel.actions.updateCurrentReport(report));
  }
};

const updateLocalROITeethIDs = (
  store: MiddlewareAPI<AppDispatch, RootState>,
  teeth: Tooth[],
) => {
  const { teethInROI, teethNotInROI } = teeth.reduce(
    (acc, tooth) => {
      const key = tooth.IsInROI ? 'teethInROI' : 'teethNotInROI';

      return {
        ...acc,
        [key]: [...acc[key], tooth.ID],
      };
    },
    { teethInROI: [], teethNotInROI: [] } as {
      teethInROI: string[];
      teethNotInROI: string[];
    },
  );

  if (teethInROI.length > 0) {
    store.dispatch(toothModel.actions.addLocalROITeethIDs(teethInROI));
  }

  if (teethNotInROI.length > 0) {
    store.dispatch(toothModel.actions.removeLocalROITeethIDs(teethNotInROI));
  }
};

export const thunkResponseMiddleware: Middleware =
  (store) => (next) => (action) => {
    const result = next(action);

    if (action.type === 'reports/setCurrentReport') {
      syncMaskFiltersWithReportSettings(store, action.payload as Report);
    }

    if (!action.type?.endsWith('/fulfilled')) {
      return result;
    }

    try {
      const { payload, type } = action as PayloadAction<ThunkPayload>;

      if (!payload) {
        return result;
      }

      switch (type) {
        // Report thunks
        case 'reports/setReportSettings/fulfilled':
          syncReportState(store, payload.Report);
          if (payload.TeethConditions?.length) {
            store.dispatch(
              conditionModel.actions.setNewestMany(payload.TeethConditions),
            );
          }
          break;

        case 'reports/setROI/fulfilled':
          if (payload.AllReportTeeth?.length) {
            store.dispatch(
              toothModel.actions.setNewestMany(payload.AllReportTeeth),
            );
            updateLocalROITeethIDs(store, payload.AllReportTeeth);
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/setReportToothComment/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/resetReportTooth/fulfilled':
          if (payload.Teeth?.length) {
            store.dispatch(toothModel.actions.setNewestMany(payload.Teeth));
          }
          if (payload.Conditions?.length) {
            store.dispatch(
              conditionModel.actions.setNewestMany(payload.Conditions),
            );
          }
          if (payload.ToothLandmarks?.length) {
            store.dispatch(
              toothLandmarksModel.actions.addMany(payload.ToothLandmarks),
            );
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/setReportToothConditionsUserDecision/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          if (payload.Conditions?.length) {
            const isUnaided = organizationModel.selectors.selectFeatureFlag(
              FeatureFlag.FeatureFlag_FDA_Unaided,
            )(store.getState());

            store.dispatch(
              conditionModel.actions.setNewestMany(
                isUnaided
                  ? dedupeConditionsByID(payload.Conditions)
                  : payload.Conditions,
              ),
            );
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/createReportToothConditions/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          if (payload.Conditions?.length) {
            store.dispatch(
              conditionModel.actions.setNewestMany(payload.Conditions),
            );
            store.dispatch(
              conditionModel.actions.addTeethGroup(payload.Conditions),
            );
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/deleteReport/fulfilled':
          dispatchEntityDeletions(store, payload);
          if (payload.Patient) {
            store.dispatch(patientModel.actions.setNewestOne(payload.Patient));
          }
          break;

        case 'reports/approveReportTooth/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          if (payload.Conditions?.length) {
            store.dispatch(
              conditionModel.actions.setNewestMany(payload.Conditions),
            );
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/disapproveReportTooth/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          if (payload.Conditions?.length) {
            store.dispatch(
              conditionModel.actions.setNewestMany(payload.Conditions),
            );
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/setToothNumerationAndRevive/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/setToothDetectionNumerationAndRevive/fulfilled':
          if (payload.Teeth?.length) {
            store.dispatch(toothModel.actions.setNewestMany(payload.Teeth));
          }
          if (payload.Conditions?.length) {
            store.dispatch(
              conditionModel.actions.setNewestMany(payload.Conditions),
            );
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/updateTeethState/fulfilled':
          if (payload.Teeth?.length) {
            store.dispatch(toothModel.actions.setNewestMany(payload.Teeth));
          }
          if (payload.Conditions?.length) {
            store.dispatch(
              conditionModel.actions.setNewestMany(payload.Conditions),
            );
          }
          if (payload.TeethLandmarks?.length) {
            store.dispatch(
              toothLandmarksModel.actions.addMany(payload.TeethLandmarks),
            );
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/markToothAsDeleted/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/markToothDetectionAsDeleted/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/addToothDisplaySlice/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
            if (payload.Tooth.IsInROI) {
              store.dispatch(
                toothModel.actions.addLocalROITeethIDs([payload.Tooth.ID]),
              );
            }
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/removeToothDisplaySlice/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/removeAllToothDisplaySlices/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/setToothMPRSliceOptions/fulfilled':
        case 'reports/setToothFullHeadMPRSliceSelection/fulfilled':
        case 'reports/setToothFullHeadMPRSliceAnnotations/fulfilled':
        case 'reports/setToothFullHeadMPRSliceViewOptions/fulfilled':
        case 'reports/setToothFullHeadMPRSliceEditorState/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/restoreToothDisplaySlices/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/generateSuggestedToothTreatment/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/setReportToothConditionUserScoreAndApproveTooth/fulfilled':
          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          if (payload.Condition) {
            store.dispatch(
              conditionModel.actions.setNewestOne(payload.Condition),
            );
          }
          syncReportState(store, payload.Report);
          break;

        case 'reports/setReportExtendedConclusion/fulfilled':
          syncReportState(store, payload.Report);
          if (payload.Teeth?.length) {
            store.dispatch(toothModel.actions.setNewestMany(payload.Teeth));
          }
          if (payload.Conditions?.length) {
            store.dispatch(
              conditionModel.actions.setNewestMany(payload.Conditions),
            );
          }
          break;

        case 'reports/approveAllTeethAndSignReport/fulfilled':
          syncReportState(store, payload.Report);
          if (payload.Teeth?.length) {
            store.dispatch(toothModel.actions.setNewestMany(payload.Teeth));
          }
          if (payload.Conditions?.length) {
            store.dispatch(
              conditionModel.actions.setNewestMany(payload.Conditions),
            );
          }
          break;

        case 'reports/setReportMedicalImageViewOptions/fulfilled':
          syncReportState(store, payload.Report);
          break;

        case 'reports/setImageMetaViewOptions/fulfilled':
          syncReportState(store, payload.Report);
          break;

        case 'reports/setImageMetaOrientationAngle/fulfilled':
          syncReportState(store, payload.Report);
          break;

        // Tooth thunks
        case 'tooth/updateReportROI/fulfilled':
          if (payload.AllReportTeeth?.length) {
            store.dispatch(toothModel.actions.setMany(payload.AllReportTeeth));
            updateLocalROITeethIDs(store, payload.AllReportTeeth);
          }
          syncReportState(store, payload.Report);
          break;

        // Asset thunks
        case 'assets/deleteAsset/fulfilled':
          if (payload.Asset) {
            store.dispatch(assetsModel.actions.removeOne(payload.Asset.ID));
          }
          if (payload.Result) {
            switch (payload.Result.case) {
              case 'User':
                if (payload.Result.value) {
                  store.dispatch(
                    userModel.actions.setOne(payload.Result.value),
                  );
                }
                break;
              case 'Organization':
                if (payload.Result.value) {
                  store.dispatch(
                    organizationModel.actions.setNewestOne(
                      payload.Result.value,
                    ),
                  );
                }
                break;
              case 'Tooth':
                if (payload.Result.value) {
                  store.dispatch(
                    toothModel.actions.setNewestOne(payload.Result.value),
                  );
                }
                break;
            }
          }
          break;

        case 'assets/setTopLayerAnnotations/fulfilled':
          if (payload.Asset) {
            store.dispatch(assetsModel.actions.setNewestOne(payload.Asset));
          }
          break;

        case 'assets/setMedicalImageViewOptions/fulfilled':
          if (payload.Asset) {
            store.dispatch(assetsModel.actions.setNewestOne(payload.Asset));
          }
          break;

        // Patient thunks
        case 'patient/createPatient/fulfilled':
          if (payload.Patient) {
            store.dispatch(patientModel.actions.setNewestOne(payload.Patient));
          }
          break;

        case 'patient/deletePatient/fulfilled':
          dispatchEntityDeletions(store, payload);
          break;

        // Study thunks
        case 'study/unsetStudyRawMeshViewer/fulfilled':
          if (payload.Study) {
            store.dispatch(studyModel.actions.setNewestOne(payload.Study));
          }
          break;

        case 'study/deleteStudy/fulfilled':
          dispatchEntityDeletions(store, payload);
          if (payload.ChangedPatient) {
            store.dispatch(
              patientModel.actions.setNewestOne(payload.ChangedPatient),
            );
          }
          break;

        // Storage thunks
        case 'storage/closeSession/fulfilled':
          if (payload.ReportAssets?.length) {
            store.dispatch(assetsModel.actions.setMany(payload.ReportAssets));
          }

          if (payload.Tooth) {
            store.dispatch(toothModel.actions.setNewestOne(payload.Tooth));
          }
          break;
      }
    } catch (error) {
      debug('Error in thunkResponseMiddleware:', error);
    }

    return result;
  };
